import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
// Hooks
import useDialog from 'hooks/useDialog';
import { useAppDispatch } from 'hooks/redux';
import useScrollIntoView from 'hooks/useScrollIntoView';
// Async
import UsersAsync from 'store/users/usersAsync';
// Selectors
import { selectSubscriptionModuleEnabled } from 'store/accounts/accountsSelectors';
// Types
import UserRoles from 'types/UserRoles';
// Components
import Phone, { getCallingCode, getPhoneWithoutCallingCode } from 'components/Phone';
import ListYourPropertyBenefits from './ListYourPropertyBenefits';
import ListYourPropertyHowWorks from './ListYourPropertyHowWorks';
import ConfirmCard from 'components/ConfirmCard';
import SubscriptionPackages from 'components/SubscriptionPackages';
// Mui
import { makeStyles } from '@mui/styles';
import { Box, Paper, TextField, Typography, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// Validation
import { isFieldRequired, isEmailValid } from '../../utilities/Validation';
// Utilities
import { getContent } from 'utilities/Utilities';

interface IFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  callingCode: string;
  role: UserRoles;
  notificationsEnabled: boolean;
  subscriptionPackageId?: number | null;
}

const ListYourPropertyPage:React.FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { scrollTo } = useScrollIntoView();

  const subscriptionModuleEnabled = useSelector(selectSubscriptionModuleEnabled);

  const { banner, benefits, howItWorks } = getContent('listYourPropertyPage');

  const { Dialog, openDialog, closeDialog } = useDialog();
  const { Dialog:SubscriptionPackagesDialog, openDialog:openSubscriptionPackagesDialog, closeDialog:closeSubscriptionPackagesDialog } = useDialog();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { control, handleSubmit, formState: { errors }, setValue, watch, reset } = useForm<IFormData>({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      callingCode: '',
      role: UserRoles.Owner,
      notificationsEnabled: true,
    }
  });

  const subscriptionPackageIdWatcher = watch('subscriptionPackageId');

  const setSubscriptionPackageId = (subscriptionPackageId: number) => {
    setValue('subscriptionPackageId', subscriptionPackageId);
    closeSubscriptionPackagesDialog();
  }

  const onSubmit = handleSubmit((data:IFormData) => {
    if (subscriptionModuleEnabled && !subscriptionPackageIdWatcher) {
      openSubscriptionPackagesDialog();
      return;
    }
    setIsLoading(true);

    const { callingCode, phone } = data;
    const nextData = {
      ...data,
      callingCode: getCallingCode(callingCode),
      phone: getPhoneWithoutCallingCode(callingCode, phone)
    }

    dispatch(UsersAsync.registrationRequest(nextData))
      .unwrap()
      .then(() => {
        reset();
        openDialog();
      })
      .catch(() => {
        setValue('subscriptionPackageId', null);
      })
      .finally(() => setIsLoading(false));

  });

  useEffect(() => {
    if (subscriptionPackageIdWatcher) {
      onSubmit();
    }
    // eslint-disable-next-line
  }, [subscriptionPackageIdWatcher])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <React.Fragment>
      <Dialog maxWidth="sm">
        <ConfirmCard onClose={closeDialog}>Confirmation link has been sent to your email</ConfirmCard>
      </Dialog>

      <SubscriptionPackagesDialog maxWidth="sm">
        <SubscriptionPackages setSubscriptionPackageId={setSubscriptionPackageId} onClose={closeSubscriptionPackagesDialog} />
      </SubscriptionPackagesDialog>

      <Box className={[classes.banner, classes.mainBlock].join(' ')}>
        <div className="container" style={{ height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '48px', flexDirection: { xs: 'column', md: 'row' } }}>
            <Box>
              <Typography className={classes.title}>{banner.title}</Typography>
              <Typography className={classes.text} sx={{ pt: 5 }}>
                {banner.text}
              </Typography>
              <Typography className={classes.text} sx={{ pt: 2 }}>
                {banner.hint}
              </Typography>
              {banner.button && (
                <Button
                  className={classes.button}
                  sx={{ mt: 5 }}
                  variant="outlined"
                  onClick={() => scrollTo('how-it-works')}
                >
                  {banner.button}
                </Button>
              )}
            </Box>
            <Paper sx={{ p:3, minWidth: { xs: '100%', md: '430px' } }}>
              <Typography
                display="block"
                variant="h5"
                textAlign="center"
                sx={{ pb: 2 }}
              >
                Create owner account
              </Typography>

              <form onSubmit={onSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* E-mail */}
                <Controller
                  control={control} name="email"
                  rules={{ required: isFieldRequired, pattern: isEmailValid }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      autoComplete="email"
                      label="E-mail" type="email"
                      error={Boolean(errors.email)}
                      helperText={errors.email ? errors.email.message : ''}
                      required
                    />
                  )}
                />
                {/* First Name */}
                <Controller
                  control={control} name="firstName"
                  rules={{ required: isFieldRequired }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="First name"
                      error={Boolean(errors.firstName)}
                      helperText={errors.firstName ? errors.firstName.message : ''}
                      required
                    />
                  )}
                />
                {/* Last Name */}
                <Controller
                  control={control} name="lastName"
                  rules={{ required: isFieldRequired }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Last name"
                      error={Boolean(errors.lastName)}
                      helperText={errors.lastName ? errors.lastName.message : ''}
                      required
                    />
                  )}
                />
                {/* Phone */}
                <Controller
                  control={control} name="phone"
                  rules={{ required: isFieldRequired }}
                  render={({ field:{ value, onChange } }) => (
                    <Phone
                      margin="none"
                      label="Cell phone" value={value}
                      onChange={([ callingCode, phone ]:string[]) => {
                        setValue('callingCode', callingCode);
                        onChange(phone);
                      }}
                      error={Boolean(errors.phone)}
                      helperText={errors.phone ? (errors.phone as any).message : ''}
                      required={true}
                    />
                  )}
                />

                <LoadingButton
                  type="submit"
                  color="primary"
                  variant="contained"
                  fullWidth
                  loading={isLoading}
                  sx={{ p: 1 }}
                >
                  Sign Up
                </LoadingButton>
              </form>
              <Typography variant="body2" sx={{ pt: 2 }}>
                {`By clicking “Sign up”, you acknowledge that you have read, understood, and agree with our `}
                <NavLink to="/terms-and-conditions" target="_blank" style={{ color: getContent('theme').primaryColor, textDecoration: 'none' }}>Terms</NavLink>
                {` and `}
                <NavLink to="/privacy-policy" target="_blank" style={{ color: getContent('theme').primaryColor, textDecoration: 'none' }}>Privacy Policy</NavLink>
              </Typography>
            </Paper>
          </Box>
        </div>
      </Box>

      <ListYourPropertyBenefits title={benefits.title} items={benefits.items} />
      {howItWorks && <ListYourPropertyHowWorks />}

      <Box className={classes.block}>
        <div className="container">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'center' }}>
            <Typography sx={{ fontSize: { xs: '24px', md: '48px' }, fontWeight: '500', color: '#fff' }}>
              Ready to list your property?
            </Typography>
            <Button
              onClick={scrollToTop}
              variant="contained"
              className={classes.signUpButton}
            >
              Sign up
            </Button>
          </Box>
        </div>
      </Box>
    </React.Fragment>
  );
}

export default ListYourPropertyPage;

const useStyles = makeStyles({
  banner: {
    backgroundImage: 'url("/img/listYourPropertyBg.png")',
    backgroundSize: 'cover',
    backgroundPosition: 'bottom',
  },
  mainBlock: {
    padding: '16px',
    '@media (min-width: 600px)': {
      padding: '32px',
    },
    '@media (min-width: 1240px)': {
      padding: '80px 0',
    },
  },
  block: {
    background: `linear-gradient(315deg, ${getContent('theme').secondaryColor} 0%, ${getContent('theme').primaryColor} 100%)`,
    padding: '24px 16px',
    '@media (min-width: 600px)': {
      padding: '32px',
    },
    '@media (min-width: 1240px)': {
      padding: '64px',
    },
  },
  title: {
    fontWeight: 500,
    fontSize: '40px',
    lineHeight: '120%',
    letterSpacing: '-1px',
    color: '#fff',
    '@media (min-width: 1104px)': {
      fontSize: '60px',
    }
  },
  text: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '-0.35px',
    color: '#fff',
  },
  button: {
    color: '#fff',
    borderColor: '#fff',
    transition: 'background-color 0.3s',
    '&:hover': {
      borderColor: '#fff',
      backgroundColor: 'rgba(255,255,255,0.08)',
    }
  },
  signUpButton: {
    width: '240px',
    color: getContent('theme').primaryColor,
    background: '#fff',
    transition: 'background 0.3s',
    '&:hover': {
      background: 'rgba(255,255,255,0.9)',
    }
  }
});
